import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  Grid,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../common/dialog/dialog";
import axios from "axios";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ItemsRemoved from "./items-removed";
import SelectableRadioGroup from "../../../common/selectableRadioGroup/selectable-radio-group";

// Utility: Local storage persistence
const STORAGE_KEY = "roomSearchData";

const saveToLocalStorage = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

const loadFromLocalStorage = () => {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : null;
};

const strategies = [
  "Reason for search *",
  "Was child/young person present? *",
  "Items Removed, Stored & Disposed *",
  "Further action to be taken *",
  "Child’s Comment *",
];

const RoomSearch = ({ selectedTask, handleRoomDialogClose }) => {
  const user = useSelector((state) => state.auth.user);
  const [witnessData, setWitnessData] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("Involved");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [note, setNote] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const [users, setUsers] = useState([]);
  const [incidentRecords, setIncidentRecords] = useState([]);
  const [witnessDialogOpen, setWitnessDialogOpen] = useState(false);
  const [incidentTime, setIncidentTime] = useState(() => new Date().toTimeString().slice(0, 5));
  const [dateInformed, setDateInformed] = useState("");
  const [present, setPresent] = useState(null);
  const [items, setItems] = useState([
    { name: "Cigarettes", count: 0 },
    { name: "Lighter", count: 0 },
    { name: "Alcohol", count: 0 },
    { name: "Medication", count: 0 },
    { name: "Vape ", count: 0 },
  ]);

  const allowedBehaviors = Array.isArray(incidentRecords) ? incidentRecords.map((b) => b.behavior) : [];

  // Hydrate from localStorage on mount
  useEffect(() => {
    const saved = loadFromLocalStorage();
    if (saved) {
      setWitnessData(saved.witnessData || []);
      setIncidentRecords(saved.incidentRecords || []);
      setIncidentTime(saved.incidentTime || "");
      setDateInformed(saved.dateInformed || "");
      setSelectedStrategy(saved.selectedStrategy || "");
      setDialogOpen(saved.dialogOpen || false);
    }
  }, []);

  // Persist form state to localStorage
  useEffect(() => {
    saveToLocalStorage({
      witnessData,
      incidentRecords,
      incidentTime,
      dateInformed,
      selectedStrategy,
      dialogOpen,
    });
  }, [witnessData, incidentRecords, incidentTime, dateInformed, selectedStrategy, dialogOpen]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get("/api/auth/users");
        const filteredUsers = response.data.filter((usera) => usera.id !== user?.id);
        setUsers(filteredUsers);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };
    fetchUsers();
  }, [user?.id]);

  const handleVerbalStatus = (event, newStatus) => setPresent(newStatus);
  const handleRoleChange = (event, newRole) => newRole && setRole(newRole);

  const handleProceed = () => {
    if (name.trim() === "") return alert("Please enter a name.");
    setWitnessData((prev) => [...prev, { name, role }]);
    setName("");
    setRole("Involved");
    setWitnessDialogOpen(false);
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (e) => audioChunks.current.push(e.data);
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
      setAudioURL(URL.createObjectURL(audioBlob));
    };

    mediaRecorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleSubmit = async () => {
    // Prevent duplicate strategy entries
    if (selectedStrategy && incidentRecords.some((record) => record.behavior === selectedStrategy)) {
      alert("This behavior has already been recorded.");
      return;
    }

    // Ensure there's at least audio or note content
    const hasContent = audioURL || (note && note.trim());
    if (!hasContent) {
      alert("Please provide an audio recording or a note.");
      return;
    }

    try {
      let audioUrl = null;

      // Only upload audio if provided
      if (audioURL) {
        const response = await fetch(audioURL);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("audio", blob, "recording.wav");

        const uploadRes = await fetch("/api/audio", {
          method: "POST",
          body: formData,
        });

        const result = await uploadRes.json();
        audioUrl = result.audioUrl;
      }

      // Construct new record
      const newRecord = {
        behavior: selectedStrategy || "Unspecified",
        audio: audioUrl,
        note: note?.trim() || null,
        items: items ?? [],
        present: present ?? null,
      };

      // Update state
      setIncidentRecords((prev) => [...prev, newRecord]);
      setDialogOpen(false);

      // Reset form fields
      setAudioURL(null);
      setNote("");
      setSelectedStrategy("");
    } catch (err) {
      console.error("Upload or submission failed:", err);
      alert("An error occurred while submitting. Please try again.");
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setNote("");
    setAudioURL(null);
    setSelectedStrategy("");
  };

  const handleDeleteRecord = (index) => {
    const updated = incidentRecords.filter((_, i) => i !== index);
    setIncidentRecords(updated);
  };

  const handleFormSubmit = async () => {
    try {
      const payload = {
        dateOfSearch: dateInformed,
        audioRecords: incidentRecords,
        timeOfSearch: incidentTime,
        witnessData,
        witnessId: user?.id,
        taskName: selectedTask?.taskName,
        status: "completed",
      };

      await axios.put(`/api/room/roomSearch/${selectedTask?.id}`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      localStorage.removeItem(STORAGE_KEY);
      handleRoomDialogClose();
    } catch (error) {
      console.error("Error submitting room search:", error);
    }
  };

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip label='Daily Log' size='small' sx={{ fontSize: "14px", backgroundColor: "#F2F6FF", color: "blue" }} />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Room Search</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8}>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "#333", mb: 2 }}>
            Room Search Record
          </Typography>
          <Grid container spacing={2}>
            <Grid item lg={3}>
              <Typography sx={{ fontSize: "14px" }} align='left'>
                Time of search
              </Typography>
              <TextField
                fullWidth
                type='time'
                size='small'
                value={incidentTime}
                onChange={(e) => setIncidentTime(e.target.value)}
                sx={{ mb: 3 }}
              />
            </Grid>
            <Grid item lg={3}>
              <Typography sx={{ fontSize: "14px" }} align='left'>
                Date of search
              </Typography>
              <TextField
                type='date'
                fullWidth
                size='small'
                value={dateInformed}
                onChange={(e) => setDateInformed(e.target.value)}
                sx={{ mb: 3 }}
              />
            </Grid>
          </Grid>

          <SelectableRadioGroup
            options={strategies}
            selectedValue={selectedStrategy}
            setSelectedValue={setSelectedStrategy}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <ReusableDialog
            isOpen={dialogOpen}
            onClose={handleClose}
            title={selectedStrategy}
            onSubmit={handleSubmit}
            submitText='Submit'
            cancelText='Cancel'
            disable={!audioURL && !note}
          >
            {selectedStrategy.includes("Reason") && (
              <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
                Give brief description about reason for search
              </Typography>
            )}
            {selectedStrategy.includes("Items") && <ItemsRemoved items={items} setItems={setItems} />}
            {selectedStrategy.includes("person") && (
              <>
                <Grid container>
                  <Grid item xs={12} sm={10}>
                    <Typography sx={{ fontSize: "14px", mb: 1 }}>
                      If child/young person was not present, reasons for this?
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <ToggleButtonGroup value={present} exclusive onChange={handleVerbalStatus}>
                      <ToggleButton value='Yes' color='primary' size='small'>
                        Yes
                      </ToggleButton>
                      <ToggleButton value='No' color='error' size='small'>
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
                <Typography sx={{ fontSize: "14px", mb: 1 }}>
                  how were they informed? (Include, who where and when they were informed)
                </Typography>
              </>
            )}
            {selectedStrategy.includes("Further") && (
              <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
                risk assessment reviewed, consequence imposed, police informed, keyworker session planned
              </Typography>
            )}
            {selectedStrategy.includes("Child’s") && (
              <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
                to be written or recorded by young person
              </Typography>
            )}
            <Box
              sx={{
                border: "2px dashed #ccc",
                padding: 3,
                textAlign: "center",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 180,
                mb: 2,
              }}
            >
              {!audioURL ? (
                <>
                  <Typography sx={{ color: "#757575", fontSize: "16px", mb: 1 }}>
                    Try Recording Your Sound Now
                  </Typography>
                  <MicIcon sx={{ fontSize: 50, color: "#757575", mb: 1 }} />
                  <Typography sx={{ fontSize: "14px", color: "#757575", mb: 1 }}>
                    To start recording press the <b>“Start Recording”</b> button and start recording!
                  </Typography>
                  <Button variant='contained' color='primary' onClick={recording ? stopRecording : startRecording}>
                    {recording ? "Stop Recording" : "Start Recording"}
                  </Button>
                </>
              ) : (
                <>
                  <audio controls src={audioURL} />
                  <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={() => setAudioURL(null)}>
                    Record Again
                  </Button>
                </>
              )}
            </Box>

            <Typography sx={{ fontSize: "14px", mb: 1 }}>Comment</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder='Enter your note here...'
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </ReusableDialog>

          {/* Witness Section */}
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "#333", mb: 2, mt: 3 }}>
            Witness / Involved in search
          </Typography>
          <Grid container>
            <Grid item lg={6}>
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#E3F2FD" }}>
                      <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Involved/Witness</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {witnessData.map((witness, i) => (
                      <TableRow key={i}>
                        <TableCell>{witness.name}</TableCell>
                        <TableCell>{witness.role}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <br />
              <Button color='primary' onClick={() => setWitnessDialogOpen(true)}>
                <AddIcon /> Add Another Involved / Witness
              </Button>
            </Grid>
          </Grid>

          <ReusableDialog
            isOpen={witnessDialogOpen}
            onClose={() => setWitnessDialogOpen(false)}
            title='Add Another Involved / Witness'
            onSubmit={handleProceed}
            submitText='Proceed'
            cancelText='Cancel'
          >
            <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
              Add the relevant details to the fields to add another person to the involved parties.
            </Typography>
            <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Name</Typography>
            <Autocomplete
              id='name'
              options={users}
              getOptionLabel={(option) => option?.username || ""}
              value={users.find((u) => u.username === name) || null}
              onChange={(e, newVal) => setName(newVal?.username || "")}
              renderInput={(params) => <TextField {...params} placeholder='Select a user' />}
              fullWidth
            />
            <Grid container>
              <Grid item lg={10}>
                <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Involved / Witness</Typography>
                <FormControl>
                  <RadioGroup
                    row
                    value={role}
                    onChange={(e) => handleRoleChange(e, e.target.value)}
                    sx={{ mb: 3, gap: 2 }}
                  >
                    <FormControlLabel
                      value='Involved'
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1751D0" } }} />}
                      label='Involved'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        ml: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": { backgroundColor: "#F5F5F5" },
                      }}
                    />
                    <FormControlLabel
                      value='Witness'
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1751D0" } }} />}
                      label='Witness'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": { backgroundColor: "#F5F5F5" },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </ReusableDialog>
        </Grid>

        {/* Right panel */}
        <Grid item lg={4}>
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: 2 }}>Incident Management</Typography>
            <Typography sx={{ fontSize: "14px", mb: 3 }}>Behavior Records</Typography>
            {incidentRecords.map((record, index) => (
              <Paper
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 1,
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid #ccc",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "black" }}>
                  {record.audio ? (
                    record.behavior
                  ) : (
                    <>
                      {record.behavior}
                      <br />
                      {record.note}
                    </>
                  )}
                </Typography>
                <Box>
                  {record.audio && (
                    <>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <MicIcon color='primary' />
                      </IconButton>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <CloudDownloadIcon color='primary' />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={() => handleDeleteRecord(index)}>
                    <DeleteIcon color='error' />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          background: "#fff",
          padding: 2,
          zIndex: 10,
        }}
      >
        <Button variant='outlined' color='primary' onClick={handleRoomDialogClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleFormSubmit}
          disabled={!witnessData.length || !incidentRecords.length || !incidentTime || !dateInformed}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default RoomSearch;
